import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop';
import { UserAvatar } from '../../components/me/UserAvatar';
import { GiftBanner } from '../../components/me/GiftBanner';
import { SectionRow } from '../../components/me/SectionRow';
import { HomeBriefSnippet } from '../../components/home/HomeBriefSnippet';
import { HomeActionGrid } from '../../components/home/HomeActionGrid';
import { useMe } from '../../hooks/useMe';
import { useChart } from '../../hooks/useChart';
import { useCourseState } from '../../hooks/useCourse';
import { shareLink, profileShareUrl } from '../../lib/share';
import { formatLongDate } from '../../lib/format-date';
import { takePendingDeepLink } from '../../lib/tokenRoutes';
import { CHAT_ENTRY_HREF } from '../../lib/chat-links';
import styles from './Home.module.css';

function HGreeting(): JSX.Element {
  const { i18n } = useTranslation();
  return (
    <div className={styles.greeting} data-testid="home-greeting">
      <span className={styles.greetingDate}>{formatLongDate(new Date(), i18n.language)}</span>
    </div>
  );
}

interface HArchetypeBlockProps {
  firstName: string | null | undefined;
  archetypeLabel: string | null | undefined;
  avatarUrl: string | null | undefined;
  meId: string | null | undefined;
}

function HArchetypeBlock({ firstName, archetypeLabel, avatarUrl, meId }: HArchetypeBlockProps): JSX.Element {
  const { t } = useTranslation(['home', 'common']);
  const navigate = useNavigate();
  // The archetype name (e.g. "Flame Pioneer") when the API sends one, mirroring the
  // Me masthead; the API sends none while the archetype is hidden. Then the first
  // name, then a neutral label — never the literal word "Архетип".
  const archetypeName = archetypeLabel ?? firstName ?? t('archetype.yours');
  const shareLabel = t('common:actions.share');

  function handleShare(): void {
    if (!meId) return;
    shareLink(profileShareUrl(meId), `${archetypeName} ✦`);
  }

  function openChart(): void {
    void navigate('/me/natal-chart');
  }

  return (
    // Tapping the block (avatar + archetype) opens the natal chart; the Share
    // button stops propagation so it keeps its own action.
    <div
      className={styles.archetypeBlock}
      data-testid="home-archetype"
      role="button"
      tabIndex={0}
      aria-label={t('common:brand.natalChart', { defaultValue: 'Натальная карта' })}
      style={{ cursor: 'pointer' }}
      onClick={openChart}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openChart();
        }
      }}
    >
      <UserAvatar
        url={avatarUrl}
        firstName={firstName}
        className={styles.archetypeAvatar}
        initialClassName={styles.archetypeAvatarInitial}
      />
      <div className={styles.archetypeInfo}>
        <span className={styles.archetypeKicker}>{t(archetypeLabel ? 'archetype.kicker' : 'archetype.chartKicker')}</span>
        <em className={styles.archetypeName}>{archetypeName}</em>
      </div>
      <button
        type="button"
        className={styles.archetypeShare}
        aria-label={shareLabel}
        onClick={(e) => {
          e.stopPropagation();
          handleShare();
        }}
      >
        ↗ {shareLabel}
      </button>
    </div>
  );
}

export function Home(): JSX.Element {
  const { t } = useTranslation('home');
  const { t: tCourse } = useTranslation('course');
  const nav = useNavigate();
  // Resume a landing feature deep-link that was stashed during onboarding.
  // Onboarding's final step (PushConsent) lands here on /home, not via RootRedirect.
  useEffect(() => {
    const pending = takePendingDeepLink();
    if (pending) nav(pending, { replace: true });
  }, [nav]);

  const { data: me } = useMe();
  const { data: chart } = useChart();
  // The course row appears once the state says the course is open to this user (launched, or the owner).
  const { data: course } = useCourseState();
  let courseSubtitle: string | undefined;
  if (course) {
    if (!course.enrolled) courseSubtitle = tCourse('home.start');
    else if (course.status === 'completed') courseSubtitle = tCourse('home.completed');
    else if (course.current?.kind === 'exam') courseSubtitle = tCourse('home.exam');
    else if (course.progress.lessonsTotal > 0) {
      const { lessonsDone, lessonsTotal } = course.progress;
      courseSubtitle = tCourse('home.lessonOf', { n: Math.min(lessonsDone + 1, lessonsTotal), total: lessonsTotal });
    }
  }
  const archetypeLabel = chart?.status === 'ready' ? chart.archetype?.label ?? null : null;

  return (
    <div className={styles.root} data-testid="home-page">
      <CosmosBackdrop />
      <main role="main" className={styles.content}>
        <HGreeting />
        <HArchetypeBlock
          firstName={me?.firstName}
          archetypeLabel={archetypeLabel}
          avatarUrl={me?.profileAvatarUrl}
          meId={me?.id}
        />
        <HomeBriefSnippet />
        <GiftBanner />
        <div className={styles.chartsRow}>
          <SectionRow
            icon="✦"
            label={t('chat.cta')}
            onClick={() => nav(CHAT_ENTRY_HREF)}
            data-testid="home-chat-banner"
          />
        </div>
        {course?.launched && (
          <div className={styles.courseRow}>
            <SectionRow
              icon="☾"
              label={tCourse('home.row')}
              subtitle={courseSubtitle}
              onClick={() => nav('/course')}
              data-testid="home-course-row"
            />
          </div>
        )}
        {/* Divider — design-system section header (kicker + hairline rule) that
            separates the daily forecast above from the quick-tool grid below. */}
        <div className={styles.toolsHead}>
          <span className={styles.toolsKicker}>{t('sections.tools')}</span>
          <span className={styles.toolsRule} aria-hidden="true" />
        </div>
        <HomeActionGrid />
      </main>
    </div>
  );
}
